import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { LIVE, live } from './live'
import type { GuideResponse } from './guide'
import type {
  Attachment,
  Contributor,
  DashboardAnalytics,
  FiveSAnalytics,
  FiveSEvaluation,
  FiveSEvaluationInsights,
  FiveSTemplate,
  Leaderboard,
  Meta,
  Paginated,
  MyStats,
  Review,
  Shift,
  ShiftAnalytics,
  Submission,
  SubmissionInsights,
  Task,
  User,
  UserRole,
  WorkCenter,
  WorkforceFilters,
  WorkforcePage,
} from '@/types/api'

/*
 * Query keys are declared in one place so an invalidation cannot silently miss
 * a cache entry because two call sites spelled the key differently.
 */
export const keys = {
  me: ['me'] as const,
  meta: ['meta'] as const,
  // `me` rather than `null`, so the administrator's own guide and a preview of
  // somebody else's are two entries and neither evicts the other.
  guide: (role: UserRole | null) => ['guide', role ?? 'me'] as const,
  workCenters: ['work-centers'] as const,
  shifts: (clock: string) => ['shifts', clock] as const,
  submissions: (filters: unknown) => ['submissions', filters] as const,
  submission: (id: string) => ['submission', id] as const,
  fiveSTemplate: (centerId: string) => ['five-s-template', centerId] as const,
  evaluation: (id: string) => ['five-s', 'evaluation', id] as const,
  evaluations: (filters: unknown) => ['evaluations', filters] as const,
  analyticsDashboard: (range: unknown) => ['analytics', 'dashboard', range] as const,
  analyticsFiveS: (params: unknown) => ['analytics', 'five-s', params] as const,
  contributors: (range: unknown) => ['analytics', 'contributors', range] as const,
  analyticsShifts: (params: unknown) => ['analytics', 'shifts', params] as const,
  workforce: (range: unknown) => ['analytics', 'workforce', range] as const,
  leaderboard: (range: unknown) => ['analytics', 'leaderboard', range] as const,
  myStats: ['analytics', 'me'] as const,
  myTasks: ['tasks', 'mine'] as const,
  directory: ['users', 'directory'] as const,
  notifications: ['notifications'] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
}

// ---------------------------------------------------------------------------
// Reference data — the lists every screen reads and almost nobody edits.
//
// Two kinds live here and they are cached differently on purpose. Enum labels
// and the guide change when the application is *deployed*, so they are held for
// an hour. Work centres and shifts change when an administrator edits them
// while other people are using the app, and they are held for a minute — see
// REFERENCE_STALE_TIME.
// ---------------------------------------------------------------------------

/**
 * How long a browser may go on answering from its own copy of a list an
 * administrator can change: **one minute**.
 *
 * ## Why it is not thirty
 *
 * It was. A supervisor added a work centre and nobody else could file a report
 * against it — not for want of a permission, the row was there and `/work-
 * centers` returned it, but because every other browser had fetched the list
 * earlier and React Query does not refetch inside `staleTime`, not even when
 * the window regains focus. Half an hour of that is a shift's worth of "the
 * machine is not in the dropdown", and on the terminal by the door that nobody
 * alt-tabs away from it was longer still.
 *
 * ## Why it is not zero
 *
 * Zero is the obvious fix and it is the wrong one. These lists are read on
 * nearly every screen — the report form, the queue filters, the 5S walk, the QR
 * sheet — so with no stale window at all every mount and every alt-tab back to
 * the tab would put a request on the wire. On plant wifi that is a spinner on a
 * form somebody is standing in front of, to catch a change that happens a few
 * times a year.
 *
 * ## Why a floor is needed at all
 *
 * `.reference.changed` (see `hooks/useRealtime.ts`) normally delivers the
 * change in milliseconds, which is what makes a minute affordable. But it is
 * best-effort in three separate ways: Reverb is optional per deployment
 * (`realtimeConfigured()`), the event rides the queue and waits if `queue:work`
 * is down, and a phone that was asleep in a pocket was not listening. The cache
 * is the floor under all three — the socket is the improvement, not the
 * mechanism.
 */
const REFERENCE_STALE_TIME = 60 * 1000

/**
 * ## Why an hour, when work centres are a minute
 *
 * Nothing in this reply is editable while the application is running. Every
 * field of it is built from a PHP enum — statuses, priorities, roles, months,
 * currencies; read `MetaController` — so the only thing that can change a label
 * is a deploy, and a deploy replaces the bundle holding this cache anyway.
 * There is no `.reference.changed` list for it for the same reason. Dropping it
 * to REFERENCE_STALE_TIME would put the largest reference payload on the wire
 * every minute to catch a change that cannot happen between two requests.
 *
 * The one thing that *does* change it is the language, and that is not handled
 * by expiry either: `I18nProvider` invalidates the whole cache on a switch.
 *
 * ## `enabled`, because the language switcher is also on the signed-out screens
 *
 * `/meta` sits behind `auth:sanctum`, so calling it as a guest is a guaranteed
 * 401 — and the response interceptor treats a 401 as an expired session and
 * navigates to `/prijava?nastavi=…`. From the sign-in page that is merely a
 * wasted request; from `/prijava/zaboravljena-lozinka` it would throw somebody
 * off the password-recovery screen they had just opened.
 */
export function useMeta({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.meta,
    queryFn: async () => (await api.get<{ data: Meta }>('/meta')).data.data,
    staleTime: 60 * 60 * 1000,
    enabled,
  })
}

/**
 * The user guide, for whoever is signed in — or, for an administrator, for the
 * role they asked to read.
 *
 * ## Why the language is not in the key
 *
 * The reply is composed server-side against the request's locale, so a cached
 * document belongs to one language and nothing else — the same contract
 * `/meta` carries. The refetch is not arranged here either: switching language
 * calls `queryClient.invalidateQueries()` in `I18nProvider`, which marks every
 * cached response stale and refetches the ones on screen. Keying on the locale
 * as well would work and would be a second mechanism doing the first one's job,
 * leaving a copy per language in the cache that nobody reads again.
 *
 * Cached as hard as `useMeta` for the same reason: the text changes when the
 * application is deployed, not while somebody is reading it.
 *
 * @param role a role to preview instead of one's own. The server refuses it
 *   with a 403 for anybody but an administrator, so it is offered only where
 *   the reply said it could be — see `GuideResponse.roles`.
 */
export function useGuide(role: UserRole | null = null) {
  return useQuery({
    queryKey: keys.guide(role),
    queryFn: async () =>
      (await api.get<GuideResponse>('/guide', { params: role ? { role } : undefined })).data,
    staleTime: 60 * 60 * 1000,
  })
}

export function useWorkCenters() {
  return useQuery({
    queryKey: keys.workCenters,
    queryFn: async () => (await api.get<{ data: WorkCenter[] }>('/work-centers')).data.data,
    staleTime: REFERENCE_STALE_TIME,
  })
}

/**
 * The shifts a walk can be filed under, and which one to start on.
 *
 * ## Why the clock is sent rather than inferred
 *
 * Everything the API stores is UTC and the server has no idea what time it is
 * where the machine stands — asking it to guess would suggest the night shift
 * to somebody walking a press at seven in the morning, and would be wrong by a
 * different amount for every customer outside Bosnia. The browser knows for
 * certain, because whoever is holding it is standing in the plant.
 *
 * ## Why the key carries it
 *
 * The suggestion is only as good as the moment it was computed. Keying on a
 * quarter-hour bucket means a form opened at 13:58 and still open at 14:20 has
 * re-asked, instead of holding a cached answer from the shift before — while
 * still being one cache entry for everybody who opens the form in the same
 * fifteen minutes, rather than one per second.
 */
export function useShifts() {
  const now = new Date()
  const clock = `${String(now.getHours()).padStart(2, '0')}:${String(
    Math.floor(now.getMinutes() / 15) * 15,
  ).padStart(2, '0')}`

  return useQuery({
    queryKey: keys.shifts(clock),
    queryFn: async () =>
      (
        await api.get<{ data: Shift[]; suggestedId: string | null }>('/shifts', {
          params: { at: clock },
        })
      ).data,
    /*
     * The quarter-hour bucket above is not a substitute for this. It rotates
     * the key on the clock, so a form opened at 13:58 re-asks at 14:00 — but a
     * shift an administrator adds at 13:59 stays invisible to everyone whose
     * key has not turned over yet, which is the same bug `useWorkCenters` had
     * and the reason this is a minute and not fifteen.
     */
    staleTime: REFERENCE_STALE_TIME,
  })
}

// ---------------------------------------------------------------------------
// Submissions
// ---------------------------------------------------------------------------

export interface SubmissionFilters {
  status?: string[]
  type?: string[]
  priority?: string[]
  workCenterId?: string
  assignedToId?: string
  createdById?: string
  search?: string
  from?: string
  to?: string
  overdue?: boolean
  openOnly?: boolean
  /** Late and nobody is on it — the queue a supervisor needs. */
  unassigned?: boolean
  sort?: 'newest' | 'oldest' | 'priority' | 'due'
  page?: number
  perPage?: number
}

/**
 * `enabled` is for a screen that shows submissions only when the company has
 * them — Home, which every company has. Asking anyway would earn a 403 from
 * the module middleware on every poll.
 */
export function useSubmissions(
  filters: SubmissionFilters,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: keys.submissions(filters),
    queryFn: async () =>
      (await api.get<Paginated<Submission>>('/submissions', { params: filters })).data,
    enabled,
    // The queue is the screen a supervisor leaves open. What each role sees is
    // scoped server-side and locked by tests/Feature/RoleVisibilityTest.php —
    // polling makes that scoping load-bearing rather than incidental.
    ...live(LIVE.operational),
    // Keeps the previous page on screen while the next one loads, instead of
    // flashing an empty table on every filter change.
    placeholderData: (previous) => previous,
  })
}

export function useSubmission(id: string | undefined) {
  return useQuery({
    queryKey: keys.submission(id ?? ''),
    queryFn: async () => (await api.get<{ data: Submission }>(`/submissions/${id}`)).data.data,
    enabled: Boolean(id),
    // Two people often have the same submission open — the reporter and whoever
    // is fixing it. A status change should appear on both screens.
    ...live(LIVE.operational),
  })
}

export function useCreateSubmission() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (form: FormData) => {
      const { data } = await api.post<{ data: Submission }>('/submissions', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['submissions'] })
      qc.invalidateQueries({ queryKey: ['analytics'] })
    },
  })
}

/**
 * Correct a report that has already been filed.
 *
 * JSON rather than the multipart the create uses: an edit changes what a report
 * *says*, and adding or removing evidence goes through the attachments
 * endpoint, which has a policy of its own.
 *
 * The whole form goes back every time. A partial patch would have to answer
 * what an absent `type` means when the improvement block is present, for every
 * combination — and each answer is a place for this and the create form to
 * disagree about the same record.
 */
export function useUpdateSubmission(id: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: SubmissionEditPayload) => {
      const { data } = await api.patch<{ data: Submission }>(`/submissions/${id}`, payload)

      return data.data
    },
    onSuccess: (submission) => {
      // Written straight into the cache as well as invalidated, so the detail
      // screen shows the correction on the way back rather than flashing the
      // old text while the refetch lands.
      qc.setQueryData(keys.submission(id), submission)
      qc.invalidateQueries({ queryKey: ['submissions'] })
      qc.invalidateQueries({ queryKey: ['analytics'] })
    },
  })
}

/**
 * Add evidence to a report that has already been filed.
 *
 * Multipart, and applied the moment a file is chosen rather than held until the
 * form is saved. The alternative — queueing files in state and uploading on
 * save — reads tidier and behaves worse: it needs a partial-failure story for
 * "the text saved and two of five photographs did not", on the screen least
 * able to explain it.
 */
export function useAddAttachments(submissionId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (files: File[]) => {
      const form = new FormData()
      files.forEach((file) => form.append('files[]', file))

      const { data } = await api.post<{ data: Attachment[] }>(
        `/submissions/${submissionId}/attachments`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )

      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.submission(submissionId) })
      qc.invalidateQueries({ queryKey: ['submissions'] })
    },
  })
}

/**
 * Take evidence back off a report.
 *
 * Gated by the same window as editing its words — see SubmissionPolicy — and
 * irreversible: the file leaves storage, not just the list. The screen asks
 * first for that reason.
 */
export function useDeleteAttachment(submissionId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const { data } = await api.delete<{ message: string }>(`/attachments/${attachmentId}`)

      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.submission(submissionId) })
      qc.invalidateQueries({ queryKey: ['submissions'] })
    },
  })
}

export interface SubmissionEditPayload {
  workCenterId: string
  type: string
  priority: string
  title: string | null
  description: string | null
  improvement?: {
    currentState?: string | null
    proposal?: string | null
    expectedImpact?: string | null
    estimatedDuration?: string | null
    possibleDate?: string | null
    estimatedSavings?: string | number | null
    estimatedCost?: string | number | null
  }
}

/**
 * Destroy a report and its files. Administrators only; the server enforces it.
 *
 * The cached entry is removed rather than invalidated. Invalidating would send
 * the detail screen straight back to the server for a submission that no longer
 * exists, and the 404 would surface as an error toast on top of the success one.
 */
export function useDeleteSubmission(id: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete<{ message: string; deletedFiles: number }>(
        `/submissions/${id}`,
      )
      return data
    },
    onSuccess: () => {
      qc.removeQueries({ queryKey: keys.submission(id) })
      qc.invalidateQueries({ queryKey: ['submissions'] })
      qc.invalidateQueries({ queryKey: ['analytics'] })
      qc.invalidateQueries({ queryKey: keys.myTasks })
    },
  })
}

export function useTransitionSubmission(id: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { status: string; note?: string }) => {
      const { data } = await api.post<{ data: Submission }>(
        `/submissions/${id}/transition`,
        payload,
      )
      return data.data
    },
    onSuccess: (updated) => {
      qc.setQueryData(keys.submission(id), updated)
      qc.invalidateQueries({ queryKey: ['submissions'] })
      qc.invalidateQueries({ queryKey: ['analytics'] })
    },
  })
}

export function useAssignSubmission(id: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (assignedToId: string | null) => {
      const { data } = await api.post<{ data: Submission }>(`/submissions/${id}/assign`, {
        assignedToId,
      })
      return data.data
    },
    onSuccess: (updated) => {
      qc.setQueryData(keys.submission(id), updated)
      qc.invalidateQueries({ queryKey: ['submissions'] })
    },
  })
}

/** Signed, short-lived link for a private photo or video. */
export async function fetchAttachmentUrl(attachmentId: string): Promise<string> {
  const { data } = await api.get<{ url: string }>(`/attachments/${attachmentId}/url`)
  return data.url
}

// ---------------------------------------------------------------------------
// 5S
// ---------------------------------------------------------------------------

export function useFiveSTemplate(workCenterId: string | undefined) {
  return useQuery({
    queryKey: keys.fiveSTemplate(workCenterId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<{
        data: { template: FiveSTemplate; workCenter: WorkCenter }
      }>(`/five-s/template/${workCenterId}`)
      return data.data
    },
    enabled: Boolean(workCenterId),
  })
}

/**
 * One evaluation, with the checklist it was scored against.
 *
 * Separate from `useEvaluations` because the payloads differ deliberately: the
 * list carries no criteria at all, since it returns dozens of rows and the
 * template is the same for every one of them.
 */
export function useEvaluation(id: string | undefined) {
  return useQuery({
    queryKey: keys.evaluation(id ?? ''),
    queryFn: async () =>
      (await api.get<{ data: FiveSEvaluation }>(`/five-s/evaluations/${id}`)).data.data,
    enabled: Boolean(id),
  })
}

/**
 * What one walk says, as charts.
 *
 * A second request rather than more keys on `useEvaluation`: the detail screen
 * is opened constantly and by everyone, while this costs four extra queries on
 * the server — the previous walk, its answers, the centre's history and the
 * period's peers — for a panel further down the page.
 */
export function useEvaluationInsights(id: string | undefined) {
  return useQuery({
    queryKey: ['five-s', 'insights', id ?? ''],
    queryFn: async () =>
      (await api.get<{ data: FiveSEvaluationInsights }>(`/five-s/evaluations/${id}/analytics`))
        .data.data,
    enabled: Boolean(id),
  })
}

export function useEvaluations(filters: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.evaluations(filters),
    queryFn: async () =>
      (await api.get<Paginated<FiveSEvaluation>>('/five-s/evaluations', { params: filters })).data,
    ...live(LIVE.analytics),
    placeholderData: (previous) => previous,
  })
}

export interface EvaluationPayload {
  workCenterId: string

  /**
   * The day the walk happened, `YYYY-MM-DD`.
   *
   * The server derives the period from it, so `periodYear`/`periodMonth` are no
   * longer sent alongside — the two could otherwise disagree, and every 5S
   * analytic groups by the period rather than the date.
   */
  evaluatedOn?: string

  /** Still accepted by the server, for anything queued before the date existed. */
  periodYear?: number
  periodMonth?: number

  /**
   * Who was working the centre in this period.
   *
   * Omitting the key means "not editing this" and leaves whoever is already
   * recorded alone; an empty array clears them. A draft save that carries only
   * scores must not detach the people somebody just picked.
   */
  workerIds?: string[]

  /**
   * Which shift was being worked. Omitted by a customer that defines none, and
   * by every client built before shifts existed.
   */
  shiftId?: string

  comment?: string
  status?: 'DRAFT' | 'SUBMITTED'
  answers: { itemId: string; score: number; comment?: string }[]
}

export function useSaveEvaluation() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: EvaluationPayload) => {
      const { data } = await api.post<{ data: FiveSEvaluation }>('/five-s/evaluations', payload)
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['evaluations'] })
      qc.invalidateQueries({ queryKey: ['analytics'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export function useDashboardAnalytics(range: { from?: string; to?: string }) {
  return useQuery({
    queryKey: keys.analyticsDashboard(range),
    queryFn: async () =>
      (await api.get<{ data: DashboardAnalytics }>('/analytics/dashboard', { params: range })).data
        .data,
    ...live(LIVE.analytics),
    staleTime: 2 * 60 * 1000,
  })
}

export function useFiveSAnalytics(params: { year?: number; month?: number }) {
  return useQuery({
    queryKey: keys.analyticsFiveS(params),
    queryFn: async () =>
      (await api.get<{ data: FiveSAnalytics }>('/analytics/five-s', { params })).data.data,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * 5S read by shift rather than by machine.
 *
 * Manager-only on the server, and the section that draws it is only reachable
 * from a manager-only page — so an error here is a real error rather than an
 * expected 403.
 */
export function useShiftAnalytics(params: { year?: number; month?: number }) {
  return useQuery({
    queryKey: keys.analyticsShifts(params),
    queryFn: async () =>
      (await api.get<{ data: ShiftAnalytics }>('/analytics/shifts', { params })).data.data,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * One page of the workforce table. Manager-only.
 *
 * Filtering, sorting and paging all happen on the server — see
 * `WorkforceAnalytics::page()`. Sorting a page in the client sorts *that page*,
 * which looks identical to sorting the table right up until there is a second
 * one.
 */
export function useWorkforceAnalytics(filters: WorkforceFilters) {
  return useQuery({
    queryKey: keys.workforce(filters),
    queryFn: async () =>
      (await api.get<WorkforcePage>('/analytics/workforce', { params: filters })).data,
    staleTime: 5 * 60 * 1000,
    // Keeps the table on screen while a filter is being typed, instead of
    // collapsing to a spinner on every keystroke.
    placeholderData: (previous) => previous,
  })
}

/**
 * The ranking every role can see.
 *
 * The only analytics call a worker makes about other people, and the payload is
 * lean for that reason — the server leaves out what anybody was paid.
 */
export function useLeaderboard(range: { from?: string; to?: string }) {
  return useQuery({
    queryKey: keys.leaderboard(range),
    queryFn: async () =>
      (await api.get<{ data: Leaderboard }>('/analytics/leaderboard', { params: range })).data.data,
    staleTime: 5 * 60 * 1000,
  })
}

export function useContributors(range: { from?: string; to?: string }) {
  return useQuery({
    queryKey: keys.contributors(range),
    queryFn: async () =>
      (await api.get<{ data: Contributor[] }>('/analytics/contributors', { params: range })).data
        .data,
  })
}

export function useMyStats() {
  return useQuery({
    queryKey: keys.myStats,
    queryFn: async () => (await api.get<{ data: MyStats }>('/analytics/me')).data.data,
    ...live(LIVE.operational),
  })
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export interface AppNotification {
  id: string
  title: string
  body: string
  context: string | null
  icon: string
  path: string | null
  isRead: boolean
  at: string | null
}

export function useNotifications(unreadOnly = false) {
  return useQuery({
    queryKey: [...keys.notifications, unreadOnly],
    queryFn: async () =>
      (await api.get<Paginated<AppNotification>>('/notifications', { params: { unreadOnly } })).data,
    ...live(LIVE.operational),
  })
}

/**
 * Drives the badge.
 *
 * Polled rather than pushed: web sockets would mean running and supervising a
 * broadcast server for a plant of a few dozen people, and a minute of latency
 * on "you have a new task" costs nothing. Paused while the tab is hidden so a
 * forgotten tab does not poll all night.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: keys.unreadCount,
    queryFn: async () => (await api.get<{ count: number }>('/notifications/unread-count')).data.count,
    ...live(LIVE.operational),
  })
}

export function useMarkNotificationRead() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.notifications })
      qc.invalidateQueries({ queryKey: keys.unreadCount })
    },
  })
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async () => (await api.post('/notifications/read-all')).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.notifications })
      qc.invalidateQueries({ queryKey: keys.unreadCount })
    },
  })
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export interface DirectoryUser {
  id: string
  name: string
  employeeCode: string
  role: string
  roleLabel: string
}

/** Assignee picker. Managers only — the server enforces it too. */
/**
 * People work can be assigned to.
 *
 * Pass a work centre to narrow it to the people who actually work that machine.
 * The server falls back to everyone when the machine has nobody assigned — a
 * centre created by hand here, or any centre before the first MES sync — and
 * says which happened in `scope`, so the caller can label a short list honestly
 * rather than implying it is complete.
 */
export function useAssignableUsers(enabled = true, workCenterId?: string) {
  return useQuery({
    queryKey: [...keys.directory, workCenterId ?? 'all'],
    queryFn: async () =>
      (
        await api.get<{ data: DirectoryUser[]; scope: 'all' | 'workCenter' }>('/users', {
          params: workCenterId ? { workCenterId } : undefined,
        })
      ).data,
    staleTime: 10 * 60 * 1000,
    enabled,
  })
}

// ---------------------------------------------------------------------------
// Reviews
// ---------------------------------------------------------------------------

/**
 * Record what an accepted proposal actually achieved.
 *
 * Separate from the report's own update because it is a different act: not a
 * correction of what was proposed, but the outcome of it, recorded by a manager
 * after the work — and unlike an edit, still possible once the record is closed.
 */
export function useRecordRealisation(submissionId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: RealisationPayload) => {
      const { data } = await api.post<{ data: Submission }>(
        `/submissions/${submissionId}/realisation`,
        payload,
      )

      return data.data
    },
    onSuccess: (submission) => {
      qc.setQueryData(keys.submission(submissionId), submission)
      qc.invalidateQueries({ queryKey: ['submissions'] })
      // The figures feed the dashboard tile and the improvement funnel, which
      // were the whole reason for this — they must not stay stale behind it.
      qc.invalidateQueries({ queryKey: ['analytics'] })
    },
  })
}

export interface RealisationPayload {
  implementedAt: string | null
  realisedById: string | null
  actualSavings: string | number | null
  actualCost: string | number | null
}

export interface ReviewPayload {
  decision: 'PENDING' | 'ACCEPTED' | 'REJECTED'
  innovationPoints?: number | null
  materialBenefitPoints?: number | null
  nonMaterialPoints?: number | null
  totalPoints?: number | null
  award?: number | null
  comment?: string | null
}

export function useCreateReview(submissionId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: ReviewPayload) => {
      const { data } = await api.post<{ data: Review }>(
        `/submissions/${submissionId}/reviews`,
        payload,
      )
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.submission(submissionId) })
      // Acceptance rate and awards on the dashboard both move.
      qc.invalidateQueries({ queryKey: ['analytics'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

/** `enabled` for the same reason as on `useSubmissions` — tasks are that module's. */
export function useMyTasks(openOnly = true, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...keys.myTasks, openOnly],
    queryFn: async () =>
      (await api.get<Paginated<Task>>('/tasks/mine', { params: { openOnly } })).data,
    enabled,
    ...live(LIVE.operational),
  })
}

export interface TaskPayload {
  assignedToId: string
  title: string
  description?: string | null
  dueDate?: string | null
}

export function useCreateTask(submissionId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: TaskPayload) => {
      const { data } = await api.post<{ data: Task }>(
        `/submissions/${submissionId}/tasks`,
        payload,
      )
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.submission(submissionId) })
      qc.invalidateQueries({ queryKey: keys.myTasks })
    },
  })
}

/**
 * The same thing, for a form where the submission is one of the answers.
 *
 * `useCreateTask` binds the submission when the hook is created, which is right
 * on a submission's own screen and impossible on `/zadaci` — there the
 * submission is picked inside the form, so it has to arrive with the payload.
 */
export function useCreateTaskOnSubmission() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ submissionId, ...payload }: TaskPayload & { submissionId: string }) => {
      const { data } = await api.post<{ data: Task }>(
        `/submissions/${submissionId}/tasks`,
        payload,
      )
      return data.data
    },
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: keys.submission(task.submissionId) })
      qc.invalidateQueries({ queryKey: keys.myTasks })
    },
  })
}

export function useUpdateTask() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; status?: string; dueDate?: string }) => {
      const { data } = await api.patch<{ data: Task }>(`/tasks/${id}`, payload)
      return data.data
    },
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: keys.myTasks })
      qc.invalidateQueries({ queryKey: keys.submission(task.submissionId) })
    },
  })
}

/**
 * What one report says, as charts.
 *
 * A second request rather than more keys on `useSubmission`: the detail screen
 * is opened constantly, while this costs several extra queries on the server —
 * the status events, two hundred comparable reports, the accepted-proposal
 * distribution — for a panel further down the page.
 */
export function useSubmissionInsights(id: string | undefined) {
  return useQuery({
    queryKey: ['submissions', 'insights', id ?? ''],
    queryFn: async () =>
      (await api.get<{ data: SubmissionInsights }>(`/submissions/${id}/analytics`)).data.data,
    enabled: Boolean(id),
  })
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export async function fetchMe(): Promise<User> {
  const { data } = await api.get<{ user: User }>('/auth/me')
  return data.user
}

// ---------------------------------------------------------------------------
// Sessions — where this account is signed in
// ---------------------------------------------------------------------------

export interface DeviceSession {
  id: string
  name: string
  isCurrent: boolean
  lastUsedAt: string | null
  createdAt: string | null
  expiresAt: string | null
}

export function useSessions() {
  return useQuery({
    queryKey: ['sessions'],
    queryFn: async () => (await api.get<{ data: DeviceSession[] }>('/auth/sessions')).data.data,
    // Short, because the point of this screen is to answer "what is signed in
    // right now" — most often while worrying about a phone left somewhere.
    staleTime: 10_000,
  })
}

export function useRevokeSession() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ message: string; wasCurrent: boolean }>(
        `/auth/sessions/${id}`,
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  })
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ message: string; revoked: number }>(
        '/auth/sessions/revoke-others',
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  })
}

